import * as Dialog from '@radix-ui/react-dialog';
import { ReactNode } from 'react';
import { cn } from '../utils/className';

export interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  /** `brand` puts the title on the app bar gradient. */
  titleTone?: 'plain' | 'brand';
  children: ReactNode;
  className?: string;
  /** `false` fits the content to the sheet instead of scrolling it. */
  scrollable?: boolean;
}

/**
 * BottomSheet component for mobile interfaces.
 * Slides up from the bottom of the screen, perfect for multiselect options on mobile.
 */
export function BottomSheet({
  open,
  onOpenChange,
  title,
  titleTone = 'plain',
  children,
  className,
  scrollable = true,
}: BottomSheetProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          data-bottom-sheet-overlay
          className='fixed inset-0 z-90 bg-black/50'
        />
        <Dialog.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          data-bottom-sheet
          className={cn(
            `bg-surface-primary border-border-secondary fixed right-0 bottom-0
            left-0 z-90 flex h-[62dvh] flex-col overflow-hidden rounded-t-2xl
            border border-b-0 shadow-xl`,
            className
          )}
        >
          {/* Header */}
          {title && titleTone === 'plain' && (
            <div className='flex flex-col bg-white'>
              <div className='flex items-center justify-center px-4 pt-4 pb-4'>
                <Dialog.Title className='text-paragraph-01 text-focus font-bold'>
                  {title}
                </Dialog.Title>
              </div>
            </div>
          )}
          {title && titleTone === 'brand' && (
            <div
              className='from-surface-focus/75 to-surface-focus flex shrink-0
                items-center justify-center bg-linear-to-r px-4 py-4'
            >
              <Dialog.Title className='size-h4 text-inverted font-bold'>
                {title}
              </Dialog.Title>
            </div>
          )}

          {/* Content */}
          <div
            className={cn(
              'min-h-0 flex-1',
              scrollable ? 'overflow-y-auto' : 'overflow-hidden'
            )}
          >
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
